/**
 * useRinascentePendingCount — count pratiche Rinascente `pending_review` + badge "nuovi".
 *
 * Ricalca il comportamento di usePendingCount + useNewSince di Casadei:
 * - `count`: totale pending_review (badge informativo)
 * - `newCount`: record creati dopo l'ultima visita alla pagina Rinascente
 *   (badge arancione che si azzera visitando la lista)
 *
 * `markRinascenteSeen()` va chiamato al mount della pagina RinascenteList per azzerare
 * il badge "nuovi".
 *
 * Polling ogni 60 s + refresh su evento "section:seen".
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchRinascenteCount } from "../api/client";

const LS_KEY = "ts_seen_rinascente";
const POLL_MS = 60_000;

export function markRinascenteSeen(): void {
  localStorage.setItem(LS_KEY, new Date().toISOString());
  window.dispatchEvent(new CustomEvent("section:seen", { detail: "rinascente" }));
}

/**
 * @param enabled - esegue il polling solo se il tenant corrente ha il modulo
 *   rinascente. Evita chiamate a /rinascente/count senza X-Tenant-ID (es.
 *   platform_admin prima di scegliere il cliente) che il BE respinge con 400.
 */
export function useRinascentePendingCount(enabled: boolean = true) {
  const [count, setCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    const tsSeen = localStorage.getItem(LS_KEY);
    const [totalRes, newRes] = await Promise.all([
      fetchRinascenteCount("pending_review").catch(() => null),
      fetchRinascenteCount("pending_review", tsSeen ?? new Date(0).toISOString()).catch(() => null),
    ]);
    if (totalRes) setCount(totalRes.pending_review);
    if (newRes) setNewCount(newRes.new_since);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    load();
    timer.current = setInterval(load, POLL_MS);
    const onSeen = () => load();
    window.addEventListener("section:seen", onSeen);
    return () => {
      if (timer.current) clearInterval(timer.current);
      window.removeEventListener("section:seen", onSeen);
    };
  }, [load, enabled]);

  return { count, newCount, refresh: load };
}
